package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.model.AttentionItem
import com.dotdrives.repository.DonationRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import kotlin.time.Clock
import kotlin.time.Instant

data class NeedsAttentionUiState(
    val loading: Boolean = false,
    val items: List<AttentionItem> = emptyList(),
    /** The instant `attentionQueue` was actually called with -- the single source of "now"
     * for both which rows the repository selected and any "unpaid N days" copy derived from
     * them, so a component can't disagree with the query by reading its own wall clock. */
    val asOf: Instant? = null,
    val error: String? = null,
)

/**
 * Both categories the schema actually backs — `donations.qbo_sync_error` and a pledge
 * unpaid past `expected_by` — combined into one queue. `has_perm_strict` means a `field`
 * member's load legitimately returns an empty queue; that is not an error and must render
 * as an empty state, not a failure banner.
 */
class NeedsAttentionController(
    private val repo: DonationRepository,
    scope: CoroutineScope,
    private val orgId: String,
    private val clock: () -> Instant = { Clock.System.now() },
) : DotController<NeedsAttentionUiState>(NeedsAttentionUiState(), scope) {
    private var generation = 0L

    fun load() {
        val gen = nextGeneration()
        val asOf = clock()
        update { it.copy(loading = true, error = null) }
        scope.launch {
            try {
                val result = repo.attentionQueue(orgId, asOf)
                if (!isCurrent(gen)) return@launch
                result
                    .onSuccess { items ->
                        update { if (isCurrent(gen)) it.copy(loading = false, items = items, asOf = asOf, error = null) else it }
                    }
                    .onFailure { error ->
                        if (error is CancellationException) throw error
                        update { if (isCurrent(gen)) it.copy(loading = false, error = error.message ?: "Something went wrong") else it }
                    }
            } catch (error: Exception) {
                if (error is CancellationException) throw error
                if (!isCurrent(gen)) return@launch
                update { if (isCurrent(gen)) it.copy(loading = false, error = error.message ?: "Something went wrong") else it }
            }
        }
    }

    fun retry() = load()

    private fun nextGeneration(): Long {
        generation += 1
        return generation
    }

    private fun isCurrent(generation: Long): Boolean = generation == this.generation
}
